import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Split } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { SplitStatusChip } from '@/shared/ui/chips/SplitStatusChip';
import { extractErrorMessage } from '@/shared/api/errors';
import { useSplitsList } from '@/entities/splits';
import { formatUsd } from '@/shared/utils/currency';
import { formatDateTime } from '@/shared/utils/date';
import type { ApiSplitsGetItem } from '@/shared/types/api/splits';

export const WidgetsSplitsMain = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [search, setSearch] = useState('');

  const query = useSplitsList({});
  const rows = useMemo(() => query.data ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      row.parcel.trek_id.toLowerCase().includes(term),
    );
  }, [rows, search]);

  const columns = useMemo<ColumnDef<ApiSplitsGetItem>[]>(
    () => [
      {
        accessorKey: 'trek_id',
        header: t('splits.list.columns.trekId'),
        cell: (info) => (
          <span className="block max-w-[200px] truncate font-medium text-brand-600">
            {info.row.original.parcel.trek_id}
          </span>
        ),
      },
      {
        accessorKey: 'parcel',
        header: t('splits.list.columns.parcel'),
        cell: (info) => (
          <span className="block max-w-[260px] truncate text-ink">
            {info.row.original.parcel.name}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: t('splits.list.columns.status'),
        cell: (info) => <SplitStatusChip status={info.row.original.status} />,
      },
      {
        accessorKey: 'targets',
        header: t('splits.list.columns.targets'),
        cell: (info) => (
          <span className="font-medium">{info.row.original.targets.length}</span>
        ),
      },
      {
        accessorKey: 'parcel_total',
        header: t('splits.list.columns.total'),
        cell: (info) => formatUsd(Number(info.row.original.parcel_total)),
      },
      {
        accessorKey: 'created_at',
        header: t('splits.list.columns.createdAt'),
        cell: (info) => formatDateTime(info.row.original.created_at),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('splits.list.title')}
        subtitle={t('splits.list.subtitle')}
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('splits.list.searchPlaceholder')}
        />

        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('splits.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <DataTable
            data={filteredRows}
            columns={columns}
            isLoading={query.isLoading}
            emptyTitle={t('splits.list.emptyTitle')}
            emptyDescription={t('splits.list.emptyDescription')}
            emptyIcon={<Split className="h-8 w-8" aria-hidden />}
            getRowId={(row) => row.id}
            onRowClick={(row) => navigate(`/splits/${row.id}`)}
            numbered
          />
        )}
      </div>
    </>
  );
};
